import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ShieldCheck, Plus, Check, X, FileText, Archive, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_STYLES = {
  Pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  Rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ job_card_id: "", job_card_number: "", request_message: "" });
  const [responding, setResponding] = useState({});
  const [responseMsg, setResponseMsg] = useState({});

  const canCreate = ["CI", "CS", "CM"].includes(user?.role);
  const canRespond = ["SM", "CM"].includes(user?.role);

  useEffect(() => { loadApprovals(); }, []);

  const loadApprovals = async () => {
    try {
      const { data } = await api.get("/approvals");
      setApprovals(data || []);
      // Viewing the page marks approvals as seen → update badge
      window.dispatchEvent(new Event("notif-changed"));
    } catch { toast.error("Failed to load approvals"); }
    setLoading(false);
  };

  const createApproval = async () => {
    if (!form.job_card_number || !form.request_message) {
      toast.error("Fill all fields");
      return;
    }
    try {
      // Look up the actual job card ID by number
      let jobCardId = form.job_card_id;
      try {
        const { data } = await api.get("/job-cards");
        const match = (data.items || []).find(j => j.job_card_number === form.job_card_number);
        if (match) jobCardId = match.id;
      } catch { /* use form value as fallback */ }

      await api.post("/approvals", { ...form, job_card_id: jobCardId });
      toast.success("Approval request created");
      setDialogOpen(false);
      setForm({ job_card_id: "", job_card_number: "", request_message: "" });
      loadApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const respond = async (id, status) => {
    setResponding((p) => ({ ...p, [id]: true }));
    try {
      await api.put(`/approvals/${id}/respond`, {
        status,
        response_message: responseMsg[id] || "",
      });
      toast.success(`Request ${status.toLowerCase()}`);
      loadApprovals();
      window.dispatchEvent(new Event("notif-changed"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setResponding((p) => ({ ...p, [id]: false }));
  };

  const addToJC = async (id) => {
    setResponding((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/approvals/${id}/add-to-jc`);
      toast.success("Surcharge approval added to Job Card notes");
      loadApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update JC");
    }
    setResponding((p) => ({ ...p, [id]: false }));
  };

  const archiveApproval = async (id) => {
    try {
      await api.put(`/approvals/${id}/archive`);
      toast.success("Approval archived");
      loadApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const deleteApproval = async (id) => {
    if (!window.confirm("Are you sure you want to delete this approval?")) return;
    try {
      await api.delete(`/approvals/${id}`);
      toast.success("Approval deleted");
      loadApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const isCM = user?.role === "CM";

  if (loading) return <div className="text-slate-500 p-8">Loading...</div>;

  return (
    <div data-testid="approvals-page" className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <ShieldCheck size={24} className="text-teal-600" /> Surcharge Approvals
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage surcharge requests and approvals</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-approval-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
                <Plus size={16} className="mr-1" /> New Request
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-slate-200">
              <DialogHeader>
                <DialogTitle className="font-heading">New Surcharge Request</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Job Card Number</Label>
                  <Input
                    data-testid="approval-jc-number"
                    value={form.job_card_number}
                    onChange={(e) => setForm({ ...form, job_card_number: e.target.value, job_card_id: e.target.value })}
                    placeholder="Enter JC number"
                    className="bg-slate-100 border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Request Message</Label>
                  <Textarea
                    data-testid="approval-message"
                    value={form.request_message}
                    onChange={(e) => setForm({ ...form, request_message: e.target.value })}
                    placeholder="Describe the surcharge..."
                    className="bg-slate-100 border-slate-300 min-h-[100px]"
                  />
                </div>
                <Button data-testid="submit-approval-btn" onClick={createApproval}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                  Submit Request
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {approvals.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <ShieldCheck size={40} className="mx-auto text-slate-400 mb-3" />
          <p className="text-slate-500">No approval requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map((a) => (
            <div key={a.id} data-testid={`approval-${a.id}`}
              className="bg-white rounded-xl shadow-sm p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-heading font-bold text-slate-800">JC: {a.job_card_number}</span>
                    <Badge variant="outline" className={STATUS_STYLES[a.status]}>{a.status}</Badge>
                  </div>
                  <p className="text-sm text-slate-700">{a.request_message}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    By {a.created_by_name} - {formatDate(a.created_at)}
                  </p>
                </div>
              </div>

              {a.response_message && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Response from {a.responded_by}:</p>
                  <p className="text-sm text-slate-800">{a.response_message}</p>
                </div>
              )}

              {canCreate && a.status === "Approved" && !a.transferred_to_jc && (
                <div className="pt-3 border-t border-slate-200">
                  <Button
                    data-testid={`jc-update-btn-${a.id}`}
                    onClick={() => addToJC(a.id)}
                    disabled={responding[a.id]}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <FileText size={14} className="mr-1" /> JC Update - Add Surcharge to Job Card
                  </Button>
                </div>
              )}

              {a.transferred_to_jc && (
                <div className="pt-3 border-t border-slate-200">
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    Transferred to JC Notes
                  </Badge>
                </div>
              )}

              {canRespond && a.status === "Pending" && (
                <div className="pt-3 border-t border-slate-200 space-y-2">
                  <Input
                    data-testid={`response-msg-${a.id}`}
                    placeholder="Response message (optional)"
                    value={responseMsg[a.id] || ""}
                    onChange={(e) => setResponseMsg((p) => ({ ...p, [a.id]: e.target.value }))}
                    className="bg-slate-100 border-slate-300 text-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      data-testid={`approve-btn-${a.id}`}
                      onClick={() => respond(a.id, "Approved")}
                      disabled={responding[a.id]}
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button
                      data-testid={`reject-btn-${a.id}`}
                      onClick={() => respond(a.id, "Rejected")}
                      disabled={responding[a.id]}
                      size="sm"
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}

              {isCM && (
                <div className="pt-3 border-t border-slate-200 flex gap-2 justify-end">
                  <Button
                    data-testid={`archive-btn-${a.id}`}
                    onClick={() => archiveApproval(a.id)}
                    size="sm"
                    variant="outline"
                    className="border-slate-300 text-slate-500 hover:bg-slate-50"
                  >
                    <Archive size={13} className="mr-1" /> Archive
                  </Button>
                  <Button
                    data-testid={`delete-approval-btn-${a.id}`}
                    onClick={() => deleteApproval(a.id)}
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 size={13} className="mr-1" /> Delete
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
